// MIDAS V3 — Per-turn stats row (MODEL-PICKER-11, spec §14.3)
//
// One compact mono chip-strip under a completed answer whose turn carried an
// `event: stats` frame. Display subset: model (+ thinking flag), TTFT, total
// latency, tokens in/out, est cost, plus (v1.2 §15.3/§15.4) a "via OpenAI"
// tag when `backend_served === "openai"` — no tag for claude/claude_pool,
// raw muted tag for unknown values, nothing when absent.
// The FULL 13-field frame is retained
// VERBATIM in state (message.modelStats) — the remaining fields sit behind a
// title-tooltip on the row (smallest thing that satisfies "full frame
// retained"; the tooltip prints the verbatim field names, no renames).
//
// Edge cases (bot-specified, §14.3): a REFUSAL turn still emits stats
// (refusal: true) — rendered as a visible flag; a HARD ERROR emits NO stats —
// the caller simply doesn't mount this (no placeholder); un-honored picks
// emit none — absence is normal. `.cc`-gated by the CALLER (isCcDevSurface)
// together with the picker.
//
// Values are dev-facing playground telemetry → mono technical abbreviations,
// not translated prose (only the refusal flag + aria label go through i18n).
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useMemo } from "react";

import { ModelTurnStats } from "../../../../shared/chatBot/models/chatBotModels";
import useTranslation from "../../../../shared/hooks/useTranslation";

export interface TurnStatsRowV3Props {
  stats: ModelTurnStats;
}

const isNum = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);

/** 842 → "842ms", 12340 → "12.3s" — keeps the strip short on slow turns. */
const fmtMs = (v: number): string =>
  v >= 10_000 ? `${(v / 1000).toFixed(1)}s` : `${Math.round(v)}ms`;

const fmtCost = (v: number): string =>
  `$${v.toFixed(v < 0.01 ? 4 : 3)}`;

/** Verbatim key=value dump of the full frame for the title tooltip. */
const buildTooltip = (stats: ModelTurnStats): string => {
  const FIELD_ORDER: Array<keyof ModelTurnStats> = [
    "timestamp_utc",
    "thread_id",
    "model",
    "thinking",
    "model_override",
    "time_to_first_token_ms",
    "total_latency_ms",
    "input_tokens",
    "output_tokens",
    "est_cost_usd",
    "citation_tag_count",
    "truncated",
    "refusal",
    "backend_served",
  ];
  return FIELD_ORDER.filter((k) => stats[k] !== undefined)
    .map((k) => `${k}=${String(stats[k])}`)
    .join("\n");
};

const TurnStatsRowV3: React.FC<TurnStatsRowV3Props> = ({ stats }) => {
  const { translate } = useTranslation("chatBot");

  const rowAria = translate("midas.v3.stats.rowAria", {
    defaultValue: "Turn statistics",
  }) as string;
  const refusalLabel = translate("midas.v3.stats.refusal", {
    defaultValue: "refusal",
  }) as string;
  // v1.2 (§15.3): shown ONLY when backend_served === "openai".
  const viaOpenaiLabel = translate("midas.v3.stats.viaOpenai", {
    defaultValue: "via OpenAI",
  }) as string;
  // MODEL-PICKER-OSS: shown ONLY when backend_served === "oss" (the RunPod-
  // hosted vLLM endpoint running our own open model).
  const viaOssLabel = translate("midas.v3.stats.viaOss", {
    defaultValue: "via RunPod (OSS)",
  }) as string;

  const items = useMemo(() => {
    const out: Array<{ key: string; text: string; accent?: boolean }> = [];
    if (typeof stats.model === "string" && stats.model.length > 0) {
      out.push({ key: "model", text: stats.model });
    }
    if (stats.thinking === true) {
      out.push({ key: "thinking", text: "thinking", accent: true });
    }
    if (isNum(stats.time_to_first_token_ms)) {
      out.push({
        key: "ttft",
        text: `ttft ${fmtMs(stats.time_to_first_token_ms)}`,
      });
    }
    if (isNum(stats.total_latency_ms)) {
      out.push({ key: "total", text: `total ${fmtMs(stats.total_latency_ms)}` });
    }
    if (isNum(stats.input_tokens) || isNum(stats.output_tokens)) {
      const tin = isNum(stats.input_tokens) ? String(stats.input_tokens) : "–";
      const tout = isNum(stats.output_tokens)
        ? String(stats.output_tokens)
        : "–";
      out.push({ key: "tok", text: `tok ${tin}→${tout}` });
    }
    if (isNum(stats.est_cost_usd)) {
      out.push({ key: "cost", text: fmtCost(stats.est_cost_usd) });
    }
    // v1.2 (§15.3/§15.4) + MODEL-PICKER-OSS: backend disclosure. "openai" →
    // localized "via OpenAI" tag; "oss" → localized "via RunPod (OSS)" tag;
    // "claude"/"claude_pool" → NO tag (the default is not news); absent →
    // nothing (older frames, tolerate silently); any OTHER non-empty value →
    // render the raw string as a muted tag (truthful + future-proof, no
    // mapping table to maintain, no crash, no console spam).
    if (
      typeof stats.backend_served === "string" &&
      stats.backend_served.length > 0 &&
      stats.backend_served !== "claude" &&
      stats.backend_served !== "claude_pool"
    ) {
      out.push({
        key: "backend",
        text:
          stats.backend_served === "openai"
            ? viaOpenaiLabel
            : stats.backend_served === "oss"
            ? viaOssLabel
            : stats.backend_served,
      });
    }
    return out;
  }, [stats, viaOpenaiLabel, viaOssLabel]);

  if (items.length === 0 && stats.refusal !== true) return null;

  return (
    <div
      role="note"
      aria-label={rowAria}
      title={buildTooltip(stats)}
      className="mono"
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 6,
        marginTop: 2,
        fontSize: 10.5,
        letterSpacing: "0.02em",
        color: "var(--faint)",
        fontFamily: "var(--font-mono)",
      }}
    >
      {stats.refusal === true && (
        <span
          style={{
            padding: "1px 7px",
            borderRadius: 999,
            border: "1px solid var(--red)",
            color: "var(--red)",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            fontSize: 9.5,
            fontWeight: 600,
          }}
        >
          {refusalLabel}
        </span>
      )}
      {items.map((it) => (
        <span
          key={it.key}
          style={{
            padding: "1px 7px",
            borderRadius: 999,
            background: "var(--bg-soft)",
            border: "1px solid var(--border-soft)",
            color: it.accent ? "var(--accent)" : "var(--muted)",
            whiteSpace: "nowrap",
          }}
        >
          {it.text}
        </span>
      ))}
    </div>
  );
};

export default TurnStatsRowV3;
